import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';

import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { getHolidays, createHoliday, updateHoliday, deleteHoliday, getWorkCenters } from '../services/api';
import { Holiday, WorkCenter } from '../types';
import { format, eachDayOfInterval, getDate, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

const Holidays: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWorkCenter, setSelectedWorkCenter] = useState<number | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState<Holiday | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [holidayToEdit, setHolidayToEdit] = useState<Holiday | null>(null);
  
  // Estados para creación de múltiples festivos
  const [createStep, setCreateStep] = useState(1); // 1: Centro, 2: Calendario, 3: Parámetros
  const [selectedWorkCenterForCreate, setSelectedWorkCenterForCreate] = useState<number | null>(null);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarHolidays, setCalendarHolidays] = useState<Holiday[]>([]); // Festivos para el calendario
  const [loadingCalendar, setLoadingCalendar] = useState(false); // Estado de carga para el calendario
  const [holidayParams, setHolidayParams] = useState<Array<{
    date: Date;
    name: string;
    is_national: boolean;
    is_recurring: boolean;
    description: string;
  }>>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [holidaysData, workCentersData] = await Promise.all([
          getHolidays(selectedWorkCenter, selectedYear),
          getWorkCenters()
        ]);
        setHolidays(holidaysData);
        setWorkCenters(workCentersData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, selectedWorkCenter]);

  const handleCreateMultipleHolidays = async () => {
    try {
      const holidayPromises = holidayParams.map(param => {
        const holidayData = {
          name: param.name || format(param.date, 'dd/MM/yyyy', { locale: es }),
          date: format(param.date, 'yyyy-MM-dd'),
          work_center_id: Number(selectedWorkCenterForCreate),
          is_national: param.is_national,
          is_recurring: param.is_recurring,
          description: param.description
        };
        return createHoliday(holidayData);
      });
      
      const createdHolidays = await Promise.all(holidayPromises);
      setHolidays([...holidays, ...createdHolidays]);
      
      // Reset estados
      setOpenDialog(false);
      setCreateStep(1);
      setSelectedWorkCenterForCreate(null);
      setSelectedDates([]);
      setHolidayParams([]);
    } catch (error) {
      console.error('Error creating holidays:', error);
      alert('Error al crear los festivos');
    }
  };

  const handleToggleDate = (date: Date) => {
    const isSelected = selectedDates.some(d => isSameDay(d, date));
    if (isSelected) {
      setSelectedDates(selectedDates.filter(d => !isSameDay(d, date)));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateStep(1);
    setSelectedWorkCenterForCreate(null);
    setSelectedDates([]);
    setCalendarHolidays([]); // Limpiar los festivos del calendario
    setOpenDialog(true);
  };

  const handleNextStep = async () => {
    if (createStep === 1 && selectedWorkCenterForCreate) {
      // Cargar los festivos del centro seleccionado para el año del calendario
      setLoadingCalendar(true);
      try {
        const holidaysData = await getHolidays(selectedWorkCenterForCreate, calendarYear);
        setCalendarHolidays(holidaysData);
      } catch (error) {
        console.error('Error fetching calendar holidays:', error);
      } finally {
        setLoadingCalendar(false);
      }
      setCreateStep(2);
    } else if (createStep === 2 && selectedDates.length > 0) {
      // Inicializar parámetros para cada fecha seleccionada
      const params = selectedDates.map(date => ({
        date,
        name: format(date, 'dd/MM/yyyy', { locale: es }),
        is_national: false,
        is_recurring: true,
        description: ''
      }));
      setHolidayParams(params);
      setCreateStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (createStep > 1) {
      setCreateStep(createStep - 1);
    }
  };

  const handleCalendarYearChange = async (newYear: number) => {
    if (selectedWorkCenterForCreate) {
      setLoadingCalendar(true);
      try {
        const holidaysData = await getHolidays(selectedWorkCenterForCreate, newYear);
        setCalendarHolidays(holidaysData);
        setCalendarYear(newYear);
      } catch (error) {
        console.error('Error fetching calendar holidays:', error);
      } finally {
        setLoadingCalendar(false);
      }
    }
  };

  const handleCancelCreate = () => {
    setOpenDialog(false);
    setCreateStep(1);
    setSelectedWorkCenterForCreate(null);
    setSelectedDates([]);
    setHolidayParams([]);
  };

  const handleDeleteClick = (holiday: Holiday) => {
    setHolidayToDelete(holiday);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!holidayToDelete) return;
    
    try {
      await deleteHoliday(holidayToDelete.id);
      setHolidays(holidays.filter(h => h.id !== holidayToDelete.id));
      setDeleteDialogOpen(false);
      setHolidayToDelete(null);
    } catch (error) {
      console.error('Error deleting holiday:', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setHolidayToDelete(null);
  };

  const handleEditClick = (holiday: Holiday) => {
    setHolidayToEdit(holiday);
    setEditDialogOpen(true);
  };

  const handleUpdateHoliday = async () => {
    if (!holidayToEdit) return;
    
    try {
      const holidayData = {
        name: holidayToEdit.name,
        date: holidayToEdit.date,
        work_center_id: holidayToEdit.work_center_id,
        is_national: holidayToEdit.is_national,
        is_recurring: holidayToEdit.is_recurring,
        description: holidayToEdit.description
      };
      
      const updatedHoliday = await updateHoliday(holidayToEdit.id, holidayData);
      setHolidays(holidays.map(h => h.id === updatedHoliday.id ? updatedHoliday : h));
      setEditDialogOpen(false);
      setHolidayToEdit(null);
    } catch (error) {
      console.error('Error updating holiday:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setHolidayToEdit(null);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
      flex: isMobile ? 0 : undefined
    },
    {
      field: 'name',
      headerName: 'Nombre',
      flex: isMobile ? 1 : undefined,
      width: isMobile ? undefined : 160,
      minWidth: isMobile ? 80 : 160
    },
    {
      field: 'date',
      headerName: 'Fecha',
      width: 120,
      flex: isMobile ? 0 : undefined
    },
    {
      field: 'work_center_id',
      headerName: 'Centro de Trabajo',
      flex: isMobile ? 1.2 : undefined,
      width: isMobile ? undefined : 180,
      minWidth: isMobile ? 120 : 180,
      renderCell: (params) => {
        const workCenter = workCenters.find((wc: WorkCenter) => wc.id === params.value);
        return workCenter ? workCenter.name : 'N/A';
      }
    },
    {
      field: 'is_national',
      headerName: 'Tipo',
      width: 120,
      flex: isMobile ? 0 : undefined,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Nacional' : 'Local'}
          sx={{
            backgroundColor: params.value ? '#f8f9fa' : '#f0f0f0',
            color: '#1a1a1a',
            border: '1px solid #e9ecef',
            borderRadius: 0,
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 500
          }}
          size="small"
        />
      )
    },
    {
      field: 'is_recurring',
      headerName: 'Recurrente',
      width: 120,
      flex: isMobile ? 0 : undefined,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Sí' : 'No'}
          sx={{
            backgroundColor: params.value ? '#f8f9fa' : '#f0f0f0',
            color: '#1a1a1a',
            border: '1px solid #e9ecef',
            borderRadius: 0,
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 500
          }}
          size="small"
        />
      )
    },
    {
      field: 'description',
      headerName: 'Descripción',
      width: 200,
      flex: isMobile ? 0 : undefined
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: isMobile ? 90 : 120,
      flex: isMobile ? 0 : undefined,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon sx={{ fontSize: 18, color: '#666666' }} />}
          label="Editar"
          onClick={() => handleEditClick(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon sx={{ fontSize: 18, color: '#666666' }} />}
          label="Eliminar"
          onClick={() => handleDeleteClick(params.row)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'start' }} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }} mb={{ xs: 3, sm: 6 }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1,
              fontWeight: 700,
              color: '#001923',
              letterSpacing: '-0.01em',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Festivos
          </Typography>
          <Typography 
            variant="body1" 
            className="descripcion-cyan"
            sx={{ 
              fontWeight: 300,
              lineHeight: 1.6
            }}
          >
            Calendario de días no laborables
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel>Centro de Trabajo</InputLabel>
            <Select
              value={selectedWorkCenter ?? ''}
              label="Centro de Trabajo"
              onChange={(e) => setSelectedWorkCenter(e.target.value === '' ? undefined : e.target.value as number)}
            >
              <MenuItem value="">Todos los Centros</MenuItem>
              {workCenters.map((center) => (
                <MenuItem key={center.id} value={center.id}>
                  {center.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 120 } }}>
            <InputLabel>Año</InputLabel>
            <Select
              value={selectedYear}
              label="Año"
              onChange={(e) => setSelectedYear(e.target.value as number)}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            sx={{
              backgroundColor: '#004254',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#003040'
              },
              flex: { xs: 1, sm: 'none' }
            }}
          >
            Nuevo Festivo
          </Button>
        </Box>
      </Box>

      <Paper
        className="holidays-datagrid-paper"
        sx={{
          height: { xs: 'calc(100vh - 320px)', sm: 600 },
          minHeight: 400,
          width: '100%',
          backgroundColor: '#FFFFFF',
          '&.holidays-datagrid-paper .MuiDataGrid-root': {
            border: 'none',
            backgroundColor: '#FFFFFF'
          },
          '&.holidays-datagrid-paper .MuiDataGrid-columnHeaders': {
            backgroundColor: '#00B0BD',
            borderBottom: '2px solid #00B0BD',
            color: '#001923'
          },
          '&.holidays-datagrid-paper .MuiDataGrid-columnHeaderTitle': {
            color: '#001923',
            fontWeight: 600
          },
          '&.holidays-datagrid-paper .MuiDataGrid-columnHeader': {
            padding: { xs: '0 8px', sm: '0 16px' }
          },
          '&.holidays-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
            display: { xs: 'none !important', sm: 'inline-flex' }
          },
          '&.holidays-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
            display: { xs: 'none !important', sm: 'inline-flex' }
          },
          '&.holidays-datagrid-paper .MuiDataGrid-cell': {
            borderBottom: '1px solid #f0f0f0',
            color: '#001923'
          },
          '@media (max-width: 600px)': {
            '&.holidays-datagrid-paper .MuiDataGrid-actionsCell': {
              gap: '0px'
            },
            '&.holidays-datagrid-paper .MuiDataGrid-actionsCell .MuiButtonBase-root': {
              padding: '4px',
              minWidth: 'unset'
            }
          },
          '&.holidays-datagrid-paper .MuiDataGrid-row': {
            backgroundColor: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }
        }}
      >
        <DataGrid
          rows={holidays}
          columns={columns}
          columnVisibilityModel={{
            id: !isMobile,
            description: !isMobile
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu={isMobile}
        />
      </Paper>

      {/* Diálogo de creación de festivos - Múltiples pasos */}
      <Dialog open={openDialog} onClose={handleCancelCreate} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {createStep === 1 && 'Paso 1: Seleccionar Centro de Trabajo'}
          {createStep === 2 && 'Paso 2: Seleccionar Días Festivos'}
          {createStep === 3 && 'Paso 3: Configurar Parámetros'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Paso 1: Seleccionar Centro de Trabajo */}
            {createStep === 1 && (
              <FormControl fullWidth>
                <InputLabel>Centro de Trabajo</InputLabel>
                <Select
                  value={selectedWorkCenterForCreate || ''}
                  onChange={(e) => setSelectedWorkCenterForCreate(e.target.value as number)}
                  label="Centro de Trabajo"
                >
                  {workCenters.map((center) => (
                    <MenuItem key={center.id} value={center.id}>
                      {center.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Paso 2: Calendario anual */}
            {createStep === 2 && (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">
                    Selecciona los días festivos para el año {calendarYear}
                  </Typography>
                  <FormControl sx={{ minWidth: 120 }}>
                    <Select
                      value={calendarYear}
                      onChange={(e) => handleCalendarYearChange(e.target.value as number)}
                      size="small"
                      disabled={loadingCalendar}
                    >
                      {[2024, 2025, 2026, 2027, 2028].map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedDates.length} día(s) seleccionado(s)
                </Typography>
                {loadingCalendar ? (
                  <Paper sx={{ p: 2, maxHeight: 500, minHeight: 500, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography>Cargando calendario...</Typography>
                  </Paper>
                ) : (
                <Paper sx={{ p: 2, maxHeight: 500, overflow: 'auto' }}>
                  <Grid container spacing={2}>
                    {(() => {
                      const year = calendarYear;
                      const months = Array.from({ length: 12 }, (_, i) => i);
                      
                      return months.map(monthIndex => {
                        const monthStart = new Date(year, monthIndex, 1);
                        const monthEnd = new Date(year, monthIndex + 1, 0);
                        const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                        
                        // Calculate empty cells at the start
                        const firstDayOfWeek = (monthStart.getDay() + 6) % 7; // Monday = 0
                        
                        return (
                          <Grid item xs={12} sm={6} md={4} key={monthIndex}>
                            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, bgcolor: '#fafafa' }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, textAlign: 'center', color: '#001923' }}>
                                {format(monthStart, 'MMMM', { locale: es })}
                              </Typography>
                              
                              {/* Mini calendar */}
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, fontSize: '11px' }}>
                                {/* Day headers */}
                                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                                  <Box 
                                    key={day} 
                                    sx={{ 
                                      textAlign: 'center', 
                                      fontWeight: 500, 
                                      color: '#666666',
                                      p: 0.5
                                    }}
                                  >
                                    {day}
                                  </Box>
                                ))}
                                
                                {/* Empty cells for month start */}
                                {Array.from({ length: firstDayOfWeek }, (_, i) => (
                                  <Box key={`empty-${i}`} />
                                ))}
                                
                                {/* Days */}
                                {days.map(day => {
                                  const dayOfWeek = (day.getDay() + 6) % 7; // Monday = 0
                                  const isWeekend = dayOfWeek >= 5; // Saturday (5) or Sunday (6)
                                  const isSelected = selectedDates.some(d => isSameDay(d, day));
                                  
                                  // Check if this day is already a holiday
                                  const dateStr = format(day, 'yyyy-MM-dd');
                                  const isExistingHoliday = calendarHolidays.some(h => 
                                    h.date === dateStr && h.work_center_id === selectedWorkCenterForCreate
                                  );
                                  
                                  const isDisabled = isWeekend || isExistingHoliday;
                                  
                                  return (
                                    <Box
                                      key={day.toISOString()}
                                      onClick={() => !isDisabled && handleToggleDate(day)}
                                      sx={{
                                        textAlign: 'center',
                                        p: 0.5,
                                        minHeight: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 0.5,
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        fontSize: '11px',
                                        backgroundColor: 
                                          isWeekend ? '#f0f0f0' :
                                          isExistingHoliday ? '#ffebee' :
                                          isSelected ? '#00B0BD' : 
                                          'transparent',
                                        color: 
                                          isWeekend ? '#999999' :
                                          isExistingHoliday ? '#d32f2f' :
                                          isSelected ? '#001923' : 
                                          '#001923',
                                        fontWeight: isSelected ? 600 : 400,
                                        border: isSelected ? '1px solid #008A94' : '1px solid transparent',
                                        '&:hover': {
                                          backgroundColor: isDisabled ? 
                                            (isWeekend ? '#f0f0f0' : '#ffebee') :
                                            (isSelected ? '#008A94' : '#e0f7f9')
                                        },
                                        opacity: isDisabled ? 0.6 : 1
                                      }}
                                    >
                                      {getDate(day)}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          </Grid>
                        );
                      });
                    })()}
                  </Grid>
                </Paper>
                )}
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#00B0BD', border: '1px solid #008A94', borderRadius: 0.5 }} />
                        <Typography variant="caption">Seleccionado</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#f0f0f0', borderRadius: 0.5 }} />
                        <Typography variant="caption">Fin de semana</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#ffebee', borderRadius: 0.5 }} />
                        <Typography variant="caption">Festivo existente</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            {/* Paso 3: Configurar parámetros individuales */}
            {createStep === 3 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Configura los parámetros para cada festivo seleccionado ({holidayParams.length} días)
                </Typography>
                
                <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
                  {holidayParams.map((param, index) => (
                    <Paper 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        mb: 2, 
                        border: '1px solid #e0e0e0',
                        '&:hover': {
                          boxShadow: 2
                        }
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#001923', mb: 1 }}>
                            {format(param.date, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: es })}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Nombre del festivo"
                            value={param.name}
                            onChange={(e) => {
                              const newParams = [...holidayParams];
                              newParams[index].name = e.target.value;
                              setHolidayParams(newParams);
                            }}
                            placeholder={format(param.date, 'dd/MM/yyyy', { locale: es })}
                          />
                        </Grid>
                        
                        <Grid item xs={6} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={param.is_national}
                                onChange={(e) => {
                                  const newParams = [...holidayParams];
                                  newParams[index].is_national = e.target.checked;
                                  setHolidayParams(newParams);
                                }}
                              />
                            }
                            label={<Typography variant="body2">Nacional</Typography>}
                          />
                        </Grid>
                        
                        <Grid item xs={6} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={param.is_recurring}
                                onChange={(e) => {
                                  const newParams = [...holidayParams];
                                  newParams[index].is_recurring = e.target.checked;
                                  setHolidayParams(newParams);
                                }}
                              />
                            }
                            label={<Typography variant="body2">Recurrente</Typography>}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Descripción"
                            multiline
                            rows={2}
                            value={param.description}
                            onChange={(e) => {
                              const newParams = [...holidayParams];
                              newParams[index].description = e.target.value;
                              setHolidayParams(newParams);
                            }}
                            placeholder="Descripción opcional del festivo"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={handleCancelCreate}
            variant="outlined"
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#004254',
              borderColor: '#004254',
              textTransform: 'none',
              fontFamily: 'ForFutureSans, sans-serif',
              fontWeight: 700,
              borderRadius: 0,
              px: 3,
              '&:hover': { backgroundColor: '#f0f7f9', borderColor: '#004254' },
            }}
          >
            Cancelar
          </Button>
          {createStep > 1 && (
            <Button
              onClick={handlePreviousStep}
              variant="outlined"
              sx={{
                backgroundColor: '#FFFFFF',
                color: '#004254',
                borderColor: '#004254',
                textTransform: 'none',
                fontFamily: 'ForFutureSans, sans-serif',
                fontWeight: 700,
                borderRadius: 0,
                px: 3,
                '&:hover': { backgroundColor: '#f0f7f9', borderColor: '#004254' },
              }}
            >
              Anterior
            </Button>
          )}
          {createStep < 3 ? (
            <Button
              onClick={handleNextStep}
              variant="contained"
              disabled={
                (createStep === 1 && !selectedWorkCenterForCreate) ||
                (createStep === 2 && selectedDates.length === 0)
              }
              sx={{
                backgroundColor: '#004254',
                color: '#FFFFFF',
                textTransform: 'none',
                fontFamily: 'ForFutureSans, sans-serif',
                fontWeight: 700,
                borderRadius: 0,
                px: 3,
                '&:hover': { backgroundColor: '#003040' },
                '&.Mui-disabled': { backgroundColor: '#ccc', color: '#fff' },
              }}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              onClick={handleCreateMultipleHolidays}
              variant="contained"
              sx={{
                backgroundColor: '#004254',
                color: '#FFFFFF',
                textTransform: 'none',
                fontFamily: 'ForFutureSans, sans-serif',
                fontWeight: 700,
                borderRadius: 0,
                px: 3,
                '&:hover': { backgroundColor: '#003040' },
              }}
            >
              Crear {holidayParams.length} Festivo(s)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el festivo "{holidayToDelete?.name}"?
          </Typography>
          {holidayToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Fecha:</strong> {holidayToDelete.date}
              </Typography>
              <Typography variant="body2">
                <strong>Tipo:</strong> {holidayToDelete.is_national ? 'Nacional' : 'Local'}
              </Typography>
              <Typography variant="body2">
                <strong>Centro:</strong> {workCenters.find(wc => wc.id === holidayToDelete.work_center_id)?.name}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de edición de festivo */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Festivo</DialogTitle>
        <DialogContent>
          {holidayToEdit && (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                margin="normal"
                label="Nombre del Festivo"
                value={holidayToEdit.name}
                onChange={(e) => setHolidayToEdit({ ...holidayToEdit, name: e.target.value })}
              />
              
              <DatePicker
                label="Fecha"
                value={new Date(holidayToEdit.date)}
                onChange={(date) => setHolidayToEdit({ ...holidayToEdit, date: format(date!, 'yyyy-MM-dd') })}
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Centro de Trabajo</InputLabel>
                <Select
                  value={holidayToEdit.work_center_id}
                  label="Centro de Trabajo"
                  onChange={(e) => setHolidayToEdit({ ...holidayToEdit, work_center_id: e.target.value as number })}
                >
                  {workCenters.map((center) => (
                    <MenuItem key={center.id} value={center.id}>
                      {center.name} - {center.location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box sx={{ mt: 2, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={holidayToEdit.is_national}
                      onChange={(e) => setHolidayToEdit({ ...holidayToEdit, is_national: e.target.checked })}
                    />
                  }
                  label="Festivo Nacional"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={holidayToEdit.is_recurring}
                      onChange={(e) => setHolidayToEdit({ ...holidayToEdit, is_recurring: e.target.checked })}
                    />
                  }
                  label="Se repite cada año"
                />
              </Box>
              
              <TextField
                fullWidth
                margin="normal"
                label="Descripción (opcional)"
                multiline
                rows={3}
                value={holidayToEdit.description || ''}
                onChange={(e) => setHolidayToEdit({ ...holidayToEdit, description: e.target.value })}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>
            Cancelar
          </Button>
          <Button 
            onClick={handleUpdateHoliday} 
            variant="contained"
            startIcon={<EditIcon />}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Holidays;